import { computed, defineComponent, h, ref, VNode } from "vue";

export const UiColumn = defineComponent({
  name: "Column",
  props: { field: String, header: String, sortable: Boolean },
  setup() {
    return () => null;
  },
});

function getColumns(nodes: VNode[] = []) {
  return nodes.filter((node) => (node.type as any)?.name === "Column");
}

export const UiDataTable = defineComponent({
  name: "DataTable",
  props: {
    value: { type: Array, default: () => [] },
    filters: Object,
    rows: { type: Number, default: 10 },
    rowsPerPageOptions: Array,
    paginator: Boolean,
    loading: Boolean,
  },
  setup(props, { slots }) {
    const sortField = ref("");
    const sortDir = ref<1 | -1>(1);
    const currentPage = ref(1);

    const filteredRows = computed(() => {
      const term = String(
        (props.filters as any)?.global?.value || "",
      ).toLowerCase();
      let data = [...(props.value as any[])];
      if (term)
        data = data.filter((item) =>
          JSON.stringify(item).toLowerCase().includes(term),
        );
      if (sortField.value) {
        data.sort(
          (a, b) =>
            String(a?.[sortField.value] ?? "").localeCompare(
              String(b?.[sortField.value] ?? ""),
            ) * sortDir.value,
        );
      }
      return data;
    });

    const totalPages = computed(() =>
      Math.max(1, Math.ceil(filteredRows.value.length / props.rows)),
    );

    const safePage = computed(() =>
      Math.min(Math.max(currentPage.value, 1), totalPages.value),
    );

    const rows = computed(() => {
      if (!props.paginator) return filteredRows.value;
      const start = (safePage.value - 1) * props.rows;
      return filteredRows.value.slice(start, start + props.rows);
    });

    const visiblePages = computed(() => {
      const total = totalPages.value;
      const page = safePage.value;
      const start = Math.max(1, Math.min(page - 2, total - 4));
      const end = Math.min(total, start + 4);
      const pages: number[] = [];
      for (let index = start; index <= end; index += 1) pages.push(index);
      return pages;
    });

    const goToPage = (page: number) => {
      currentPage.value = Math.min(Math.max(page, 1), totalPages.value);
    };

    return () => {
      const columns = getColumns(slots.default?.() as VNode[]);
      return h(
        "div",
        {
          class:
            "overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_10px_26px_rgba(15,23,42,0.05)]",
        },
        [
          slots.header
            ? h(
                "div",
                { class: "border-b border-slate-200 bg-slate-50/80 p-4" },
                slots.header(),
              )
            : null,
          h("div", { class: "relative min-h-[180px] overflow-x-auto" }, [
            h(
              "table",
              {
                class: [
                  "min-w-full divide-y divide-slate-200 text-sm",
                  props.loading ? "opacity-45" : "",
                ],
              },
              [
                h(
                  "thead",
                  {
                    class:
                      "bg-slate-100 text-left text-sm font-bold uppercase text-slate-600",
                  },
                  h(
                    "tr",
                    columns.map((column) => {
                      const header = (column.children as any)?.header;
                      return h(
                        "th",
                        {
                          class: "whitespace-nowrap px-4 py-3.5",
                          onClick: () => {
                            const field = (column.props as any)?.field;
                            if (!field) return;
                            sortDir.value =
                              sortField.value === field
                                ? ((sortDir.value * -1) as 1 | -1)
                                : 1;
                            sortField.value = field;
                          },
                        },
                        header
                          ? header({})
                          : (column.props as any)?.header || "",
                      );
                    }),
                  ),
                ),
                h(
                  "tbody",
                  { class: "divide-y divide-slate-100 bg-white" },
                  rows.value.length
                    ? rows.value.map((row) =>
                        h(
                          "tr",
                          { class: "transition hover:bg-teal-50/45" },
                          columns.map((column) => {
                            const props = column.props as any;
                            const body = (column.children as any)?.body;
                            return h(
                              "td",
                              {
                                class: "px-4 py-3.5 text-[12px] text-slate-700",
                              },
                              body
                                ? body({ data: row })
                                : row?.[props?.field] ?? "",
                            );
                          }),
                        ),
                      )
                    : h("tr", [
                        h(
                          "td",
                          {
                            colspan: Math.max(columns.length, 1),
                            class:
                              "h-[140px] px-4 py-10 text-center text-sm font-medium text-slate-500",
                          },
                          slots.empty
                            ? slots.empty()
                            : props.loading
                            ? ""
                            : "Không có dữ liệu",
                        ),
                      ]),
                ),
              ],
            ),
            props.loading
              ? h(
                  "div",
                  {
                    class:
                      "absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]",
                  },
                  h(
                    "div",
                    {
                      class:
                        "flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-lg",
                    },
                    [
                      h("div", {
                        class:
                          "h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600",
                      }),
                      h(
                        "div",
                        {
                          class: "text-sm font-bold text-slate-600",
                        },
                        "Đang tải dữ liệu",
                      ),
                    ],
                  ),
                )
              : null,
          ]),
          props.paginator
            ? h(
                "div",
                {
                  class:
                    "flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm",
                },
                [
                  h(
                    "div",
                    { class: "font-medium text-slate-500" },
                    filteredRows.value.length
                      ? `Hiển thị ${
                          (safePage.value - 1) * props.rows + 1
                        }-${Math.min(
                          safePage.value * props.rows,
                          filteredRows.value.length,
                        )} / ${filteredRows.value.length}`
                      : "Không có dữ liệu",
                  ),
                  h("div", { class: "flex items-center gap-1" }, [
                    h(
                      "button",
                      {
                        type: "button",
                        disabled: safePage.value <= 1,
                        class:
                          "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 font-bold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50",
                        onClick: () => goToPage(safePage.value - 1),
                      },
                      h("i", { class: "pi pi-chevron-left text-xs" }),
                    ),
                    visiblePages.value.map((page) =>
                      h(
                        "button",
                        {
                          type: "button",
                          class: [
                            "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border px-3 font-bold transition",
                            page === safePage.value
                              ? "border-teal-600 bg-teal-600 text-white"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100",
                          ],
                          onClick: () => goToPage(page),
                        },
                        String(page),
                      ),
                    ),
                    h(
                      "button",
                      {
                        type: "button",
                        disabled: safePage.value >= totalPages.value,
                        class:
                          "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 font-bold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50",
                        onClick: () => goToPage(safePage.value + 1),
                      },
                      h("i", { class: "pi pi-chevron-right text-xs" }),
                    ),
                  ]),
                ],
              )
            : null,
        ],
      );
    };
  },
});

export const UiDataView = defineComponent({
  name: "DataView",
  props: {
    value: { type: Array, default: () => [] },
    rows: { type: Number, default: 20 },
    paginator: Boolean,
  },
  setup(props, { slots }) {
    return () =>
      h(
        "div",
        { class: "grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-4" },
        (props.value as any[])
          .slice(0, props.paginator ? props.rows : undefined)
          .map((data) =>
            h(
              "div",
              { class: "min-w-0" },
              (slots as any).grid
                ? (slots as any).grid({ data })
                : h("div", data),
            ),
          ),
      );
  },
});

export const UiBadge = defineComponent({
  name: "Badge",
  props: { value: [String, Number] },
  setup(props) {
    return () =>
      h(
        "span",
        {
          class:
            "inline-flex rounded-full bg-teal-100 px-2 py-1 text-xs font-bold text-teal-800",
        },
        props.value,
      );
  },
});

export const UiChart = defineComponent({
  name: "Chart",
  setup: () => () => h("div"),
});
export const UiGalleria = defineComponent({
  name: "Galleria",
  setup:
    (_, { slots }) =>
    () =>
      h("div", slots.default?.()),
});
